'use client';

import { BigNum, QUOTE_PRECISION_EXP } from '@drift-labs/sdk';
import React, { useCallback, useState, useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';
import useDriftClient from 'src/hooks/useDriftClient';
import useDriftClientIsReady from 'src/hooks/useDriftClientIsReady';
import useDriftAccountStore from 'src/stores/useDriftAccountsStore';
import { COMMON_UI_UTILS } from '@drift/common';
import useInterval from 'src/hooks/useInterval';
import ValueWithLabelCard from 'src/components/primitives/ValueWithLabelCard/ValueWithLabelCard';
import NumberDisplay from 'src/components/NumberDisplay/NumberDisplay';
import useWalletIsConnected from 'src/hooks/useWalletIsConnected';
import useAppInitialLoad from 'src/hooks/app/useAppInitialLoad';

export default function PortfolioHeaderTotalFunding() {
	const { isInitialLoading } = useAppInitialLoad();
	const connected = useWalletIsConnected();
	const [currentUserKey, userAccountNotInitialized] = useDriftAccountStore(
		useShallow((s) => [s.currentUserKey, s.userAccountNotInitialized])
	);
	const driftClient = useDriftClient();
	const driftClientIsReady = useDriftClientIsReady();

	const [cumulativeFunding, setCumulativeFunding] = useState<BigNum>();
	const [isLoading, setIsLoading] = useState(false);

	const fetchFundingData = useCallback(() => {
		if (
			!driftClient ||
			!driftClientIsReady ||
			!connected ||
			userAccountNotInitialized
		) {
			return;
		}

		try {
			const { userId, userAuthority } =
				COMMON_UI_UTILS.getIdAndAuthorityFromKey(currentUserKey || '');
			const acct = driftClient.getUserAccount(userId, userAuthority);

			setCumulativeFunding(
				BigNum.from(acct?.cumulativePerpFunding, QUOTE_PRECISION_EXP)
			);
		} catch (err) {
			setCumulativeFunding(BigNum.zero());
		} finally {
			setIsLoading(false);
		}
	}, [
		driftClient,
		driftClientIsReady,
		currentUserKey,
		connected,
		userAccountNotInitialized,
	]);

	// Fetch on account state change.
	useEffect(() => {
		if (!connected || userAccountNotInitialized) {
			setCumulativeFunding(undefined);
			setIsLoading(false);
			return;
		}

		setIsLoading(true);
		fetchFundingData();
	}, [fetchFundingData, connected, userAccountNotInitialized]);

	// Refetch every 5 seconds.
	useInterval(fetchFundingData, 5000);

	return (
		<ValueWithLabelCard.Item label="All-Time Funding" className="sm:items-end">
			<NumberDisplay
				value={cumulativeFunding}
				type="currency"
				loading={isInitialLoading || isLoading}
				skeletonClassName="w-16 h-lg sm:h-2xl"
			/>
		</ValueWithLabelCard.Item>
	);
}
